import React from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate
} from 'react-router-dom';
import { MantineProvider } from '@mantine/core';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import {
  QueryClient,
  QueryClientProvider
} from '@tanstack/react-query';
import { AbilityContext } from './components/Can';
import buildAbilityFor from './abilities/appAbility';
import Requests from './routes/Requests';
import Root from './routes/Root';
import SelectNewRequestType from './routes/SelectNewRequestType';
import NewRequest from './routes/NewRequest';
import DesignProjectForm from './components/forms/DesignProjectForm';
import WebUpdateForm from './components/forms/WebUpdateForm';
import SocialMediaPostForm from './components/forms/SocialMediaPostForm';
import EventForm from './components/forms/EventForm';
import MarketingCampaignForm from './components/forms/MarketingCampaignForm';
import BusinessCardForm from './components/forms/BusinessCardForm';
import Settings from './routes/Settings';
import { RequestsProvider } from './reducers/requestsReducer';

import '@mantine/core/styles.css';
import '@mantine/tiptap/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/dropzone/styles.css';

const queryClient = new QueryClient();

const userAbility = buildAbilityFor(MtsAppVars.currentUser);

const router = createBrowserRouter([
  {
    path: '/',
    element: <Root />,
    children: [
      {
        index: true,
        element: <Navigate to="/requests" replace />
      },
      {
        path: 'requests/new/type',
        element: <SelectNewRequestType />
      },
      {
        path: 'design_projects/new',
        element: <NewRequest
          requestType="design_project_request"
          formComponent={DesignProjectForm}
        />
      },
      {
        path: 'web_updates/new',
        element: <NewRequest
          requestType="web_update_request"
          formComponent={WebUpdateForm}
        />
      },
      {
        path: 'social_media_posts/new',
        element: <NewRequest
          requestType="social_media_post_request"
          formComponent={SocialMediaPostForm}
        />
      },
      {
        path: 'events/new',
        element: <NewRequest
          requestType="event_request"
          formComponent={EventForm}
        />
      },
      {
        path: 'marketing_campaigns/new',
        element: <NewRequest
          requestType="marketing_campaign_request"
          formComponent={MarketingCampaignForm}
        />
      },
      {
        path: 'business_cards/new',
        element: <NewRequest
          requestType="business_card_request"
          formComponent={BusinessCardForm}
        />
      },
      {
        path: 'requests',
        element: <Requests />,
      },
      {
        path: 'requests/page/:page',
        element: <Requests />
      },
      {
        path: ':requestType/:requestId',
        element: <Requests requestOpen={true} />
      },
      {
        path: 'settings',
        element: <Settings />
      }
    ]
  }
]);

export default function App() {
  return (
    <MantineProvider>
      <ModalsProvider>
        <QueryClientProvider client={queryClient}>
          <AbilityContext.Provider value={userAbility}>
            <RequestsProvider>
              <Notifications />
              <RouterProvider router={router} />
            </RequestsProvider>
          </AbilityContext.Provider>
        </QueryClientProvider>
      </ModalsProvider>
    </MantineProvider>
  );
}
